import React, { useEffect, useRef, useState } from 'react';
import { serializeEditor, isSafeLink, richTextToHtml, htmlToRichText } from './richText';

// Adds https:// when someone types "example.com".
export function normaliseLink(input) {
  const value = String(input || '').trim();
  if (!value) return '';
  if (/^(https?:\/\/|mailto:)/i.test(value)) return value;
  if (/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) return `mailto:${value}`;
  return `https://${value}`;
}

// Description editor: type normally, with Bold / Italic / Centre / Link /
// Image buttons, like writing an email. Saves Frolik's safe text format.
export default function RichTextEditor({ onChange, onUploadImage, placeholder }) {
  const editorRef = useRef(null);
  const imageInputRef = useRef(null);
  const savedRange = useRef(null);
  const [active, setActive] = useState({ bold: false, italic: false, centre: false });
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  // Link panel: { text, url, anchor } while open. anchor = existing link being edited.
  const [linkPanel, setLinkPanel] = useState(null);
  // HTML view (like Blogspot): the description as editable HTML. null = normal view.
  const [htmlSource, setHtmlSource] = useState(null);
  const htmlView = htmlSource !== null;
  const linkUrlRef = useRef(null);

  const emit = () => onChange(serializeEditor(editorRef.current));

  const refreshActive = () => {
    if (!editorRef.current || !editorRef.current.contains(document.getSelection()?.anchorNode)) return;
    try {
      setActive({
        bold: document.queryCommandState('bold'),
        italic: document.queryCommandState('italic'),
        centre: document.queryCommandState('justifyCenter'),
      });
    } catch (stateError) {
      // Some browsers don't report command state; buttons still work.
    }
  };

  // Remember the last selection inside the editor, so tapping a toolbar button
  // (which can clear the selection on phones) still knows which word was picked.
  useEffect(() => {
    const onSelectionChange = () => {
      refreshActive();
      const selection = document.getSelection();
      if (editorRef.current && selection && selection.rangeCount && editorRef.current.contains(selection.anchorNode)) {
        savedRange.current = selection.getRangeAt(0).cloneRange();
      }
    };
    document.addEventListener('selectionchange', onSelectionChange);
    return () => document.removeEventListener('selectionchange', onSelectionChange);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const saveSelection = () => {
    const selection = document.getSelection();
    if (selection && selection.rangeCount && editorRef.current.contains(selection.anchorNode)) {
      savedRange.current = selection.getRangeAt(0).cloneRange();
    }
  };

  const restoreSelection = () => {
    editorRef.current.focus();
    if (savedRange.current) {
      const selection = document.getSelection();
      selection.removeAllRanges();
      selection.addRange(savedRange.current);
    }
  };

  const run = (command, value) => {
    editorRef.current.focus();
    try {
      document.execCommand('styleWithCSS', false, false);
      document.execCommand('defaultParagraphSeparator', false, 'div');
    } catch (setupError) {
      // Older browsers: defaults are fine.
    }
    document.execCommand(command, false, value);
    emit();
    refreshActive();
  };

  // Link button: opens a small panel under the toolbar. A selected word fills
  // the Text field (still editable); the address is pasted into Link. Inside an
  // existing link, the panel edits or removes it.
  const openLinkPanel = () => {
    saveSelection();
    const range = savedRange.current;
    const node = range ? range.commonAncestorContainer : null;
    const element = node && (node.nodeType === 1 ? node : node.parentElement);
    const anchor = element && element.closest ? element.closest('a') : null;
    const insideEditor = anchor && editorRef.current.contains(anchor);

    setError('');
    setLinkPanel(insideEditor
      ? { text: anchor.textContent, url: anchor.getAttribute('href') || '', anchor }
      : { text: range ? range.toString() : '', url: '', anchor: null });
  };

  useEffect(() => {
    if (linkPanel && linkUrlRef.current) linkUrlRef.current.focus();
    // Focus the address field once, when the panel opens.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [Boolean(linkPanel)]);

  const placeCaretAfter = (node) => {
    const selection = document.getSelection();
    const range = document.createRange();
    range.setStartAfter(node);
    range.collapse(true);
    selection.removeAllRanges();
    selection.addRange(range);
    savedRange.current = range.cloneRange();
  };

  const applyLink = (event) => {
    if (event) event.preventDefault();
    const url = normaliseLink(linkPanel.url);
    if (!url || !isSafeLink(url)) {
      setError('That doesn’t look like a web or email address.');
      return;
    }
    const text = linkPanel.text.trim() || url.replace(/^mailto:/, '');

    if (linkPanel.anchor && editorRef.current.contains(linkPanel.anchor)) {
      linkPanel.anchor.setAttribute('href', url);
      linkPanel.anchor.textContent = text;
      editorRef.current.focus();
      placeCaretAfter(linkPanel.anchor);
    } else {
      const anchor = document.createElement('a');
      anchor.setAttribute('href', url);
      anchor.textContent = text;
      editorRef.current.focus();

      let range = savedRange.current;
      if (!range || !editorRef.current.contains(range.commonAncestorContainer)) {
        // Nothing picked yet: add it at the end.
        range = document.createRange();
        range.selectNodeContents(editorRef.current);
        range.collapse(false);
      }
      range.deleteContents();
      range.insertNode(anchor);
      // A space after the link (unless one follows already) so typing carries
      // on as normal text, not as part of the link.
      const next = anchor.nextSibling;
      if (next && next.nodeType === 3 && /^\s/.test(next.textContent)) {
        placeCaretAfter(anchor);
      } else {
        const space = document.createTextNode('\u00a0');
        anchor.after(space);
        placeCaretAfter(space);
      }
    }

    setError('');
    setLinkPanel(null);
    emit();
  };

  const removeLink = () => {
    const { anchor } = linkPanel;
    if (anchor && editorRef.current.contains(anchor)) {
      const text = document.createTextNode(anchor.textContent);
      anchor.replaceWith(text);
      editorRef.current.focus();
      placeCaretAfter(text);
      emit();
    }
    setLinkPanel(null);
  };

  const chooseImage = () => {
    saveSelection();
    imageInputRef.current.click();
  };

  const onImageChosen = async (event) => {
    const file = event.target.files && event.target.files[0];
    event.target.value = '';
    if (!file) return;
    setError('');
    setUploading(true);
    try {
      const url = await onUploadImage(file);
      restoreSelection();
      run('insertImage', url);
    } catch (uploadError) {
      setError(uploadError.message || 'The picture couldn’t be added.');
    } finally {
      setUploading(false);
    }
  };

  // Keep pasted text plain, so formatting and code from other sites can't sneak in.
  const onPaste = (event) => {
    event.preventDefault();
    const text = event.clipboardData.getData('text/plain');
    document.execCommand('insertText', false, text);
    emit();
  };

  // Switch between the normal editor and the HTML view. Coming back, only
  // safe formatting is kept (richText.js), and the editor is rebuilt from it.
  const toggleHtmlView = () => {
    setLinkPanel(null);
    setError('');
    if (!htmlView) {
      setHtmlSource(richTextToHtml(serializeEditor(editorRef.current)));
      return;
    }
    const text = htmlToRichText(htmlSource);
    editorRef.current.innerHTML = richTextToHtml(text);
    setHtmlSource(null);
    onChange(text);
  };

  const onHtmlChange = (event) => {
    setHtmlSource(event.target.value);
    onChange(htmlToRichText(event.target.value));
  };

  const tool = (label, title, onPress, isActive) => (
    <button
      type="button"
      className={`rte-tool${isActive ? ' is-active' : ''}`}
      title={title}
      aria-label={title}
      aria-pressed={isActive === undefined ? undefined : isActive}
      // Keep the text selection when tapping a button.
      onMouseDown={(event) => event.preventDefault()}
      onClick={onPress}
      disabled={uploading || (htmlView && title !== 'HTML view')}
    >
      {label}
    </button>
  );

  return (
    <div className="rte">
      <div className="rte-toolbar" role="toolbar" aria-label="Text formatting">
        {tool(<strong>B</strong>, 'Bold', () => run('bold'), active.bold)}
        {tool(<em>I</em>, 'Italic', () => run('italic'), active.italic)}
        {tool('≡', 'Centre', () => run(active.centre ? 'justifyLeft' : 'justifyCenter'), active.centre)}
        {tool('Link', 'Insert link', openLinkPanel, Boolean(linkPanel))}
        {tool(uploading ? 'Uploading…' : 'Image', 'Add image', chooseImage)}
        {tool('</>', 'HTML view', toggleHtmlView, htmlView)}
        <input
          ref={imageInputRef}
          type="file"
          accept="image/*"
          onChange={onImageChosen}
          className="banner-picker-input"
          tabIndex={-1}
          aria-hidden="true"
        />
      </div>

      {linkPanel && (
        <div className="rte-link-panel" role="group" aria-label="Add a link">
          <label className="rte-link-field">
            <span>Text</span>
            <input
              type="text"
              value={linkPanel.text}
              onChange={(event) => setLinkPanel({ ...linkPanel, text: event.target.value })}
              placeholder="Words people tap"
              autoComplete="off"
              onKeyDown={(event) => { if (event.key === 'Enter') applyLink(event); }}
            />
          </label>
          <label className="rte-link-field">
            <span>Link</span>
            <input
              ref={linkUrlRef}
              type="url"
              inputMode="url"
              autoCapitalize="off"
              autoCorrect="off"
              spellCheck={false}
              value={linkPanel.url}
              onChange={(event) => setLinkPanel({ ...linkPanel, url: event.target.value })}
              placeholder="Paste a link, e.g. frolik.world"
              onKeyDown={(event) => {
                if (event.key === 'Enter') applyLink(event);
                if (event.key === 'Escape') setLinkPanel(null);
              }}
            />
          </label>
          <div className="rte-link-actions">
            {linkPanel.anchor && (
              <button type="button" className="rte-link-remove" onClick={removeLink}>Remove link</button>
            )}
            <button type="button" onClick={() => { setLinkPanel(null); setError(''); }}>Cancel</button>
            <button type="button" className="is-primary" onClick={applyLink} disabled={!linkPanel.url.trim()}>
              {linkPanel.anchor ? 'Save link' : 'Add link'}
            </button>
          </div>
        </div>
      )}

      {htmlView && (
        <textarea
          className="rte-html newevent-field"
          value={htmlSource}
          onChange={onHtmlChange}
          aria-label="Description as HTML"
          placeholder="<b>bold</b> <i>italic</i> <a href=&quot;https://…&quot;>link</a>"
          spellCheck={false}
          autoCapitalize="off"
          autoCorrect="off"
        />
      )}

      <div
        ref={editorRef}
        hidden={htmlView}
        className="rte-editor newevent-field"
        contentEditable
        role="textbox"
        aria-multiline="true"
        aria-label="Description"
        data-placeholder={placeholder}
        onInput={emit}
        onBlur={saveSelection}
        onKeyUp={saveSelection}
        onMouseUp={saveSelection}
        onPaste={onPaste}
        onDrop={(event) => event.preventDefault()}
        suppressContentEditableWarning
      />

      {error && <div className="rte-error" role="alert">{error}</div>}
    </div>
  );
}
